<template>
  <div class="agency-contract">
    <sirui-search
      ref="siruiSearch"
      :query="query"
      :columns="searchColumns"
      @resetBtnClick="resetBtnClick"
      @searchBtnClick="searchBtnClick"
    ></sirui-search>
    <div class="main-content">
      <sirui-table
        ref="siruiTable"
        :buttonList="menuButtonList"
        :loading="load"
        :data="tableData"
        :paging="true"
        :setting="false"
        :customVisible="true"
        :columns="columnList"
        @dblclickEvent="dblclickEvent"
        @chooseClick="chooseClick"
        @handleSizeChange="handleSizeChange"
        @handleCurrentChange="handleCurrentChange"
        @handleSelectionChange="handleSelectionChange"
        @sortChangeEvent="sortChangeEvent"
      >
        <vxe-table-column
          v-for="(item, index) in columnList"
          :key="index"
          :type="item.type"
          :visible="item.visible"
          :fixed="item.fixed"
          :width="item.width"
          :sortable="item.sortable"
          :formatter="item.formatter"
          :field="item.field"
          :title="item.title ? $t(item.title) : null"
        >
          <template v-slot="{ row }">
            <span v-if="item.dictType">
              {{ row[item.field] | getDictNameByCode(item.dictType) }}
            </span>
            <span v-else-if="item.format">
              {{ row[item.field] | formatTableColumn(item.format) }}
            </span>
            <template v-else>{{ row[item.field] }}</template>
          </template>
        </vxe-table-column>
      </sirui-table>
      <originals-received-dialog
        ref="OriginalsReceivedDialog"
        :vFlag="'agency'"
        @confirm="resetBtnClick"
      />
    </div>
  </div>
</template>

<script>
  // 代理合同列表
  import { mapState } from 'vuex';
  import SiruiSearch from '@/components/SiruiSearch';
  import SiruiTable from '@/components/SiruiTable';
  import COMPANY_API from '@/api/masterData/company';
  import AGENCY_API from '@/api/agencyContract/index';
  import { filterEmpty, toLine, getUserCompanyList } from '@/common/js/util';
  import { handlePath, columnListDefault } from './js';
  import tableMixins from '@/common/mixins/table.js';
  import OriginalsReceivedDialog from '../../purchaseSalesCenter/components/originalsReceivedDialog.vue';

  export default {
    mixins: [tableMixins],
    name: 'agencyContractList',
    components: {
      SiruiSearch,
      SiruiTable,
      OriginalsReceivedDialog,
    },
    data() {
      return {
        columnListDefault: columnListDefault,
        load: false,
        tableData: [],
        multipleSelection: [], // 表格被选中的行数据
        checkRowKeys: [],
        menuButtonList: [], // 表格功能按钮
        query: {
          orderByDesc: 'tot.contract_create_date desc',
          originalsReceivedBusiness: null,
          originalsReceivedRm: null,
        },
        companyList: getUserCompanyList(),
        columnList: [],
      };
    },
    computed: {
      ...mapState({
        userInfo: (state) => state.userInfo.userInfo,
      }),
      // 搜索栏配置数据
      searchColumns() {
        const query = this.query;
        const departmentUrl = query.contractCompanyNo
          ? `${COMPANY_API.GET_COMPANY_DETAIL_API}/${query.contractCompanyNo}`
          : '';
        return [
          {
            type: 'input',
            prop: 'externalContractNo',
            name: this.$t('contractAgency.externalContractNo'),
          },
          {
            type: 'input',
            prop: 'contractNo',
            name: this.$t('contractAgency.contractNo'),
          },
          {
            type: 'dictSelect',
            prop: 'tradeType',
            name: this.$t('contractAgency.tradeType'),
            apiData: { dictType: 'proxy_trade_type' },
            clearable: true,
          },
          {
            type: 'dictSelect',
            prop: 'businessType',
            name: this.$t('contractAgency.businessNature'),
            apiData: { dictType: 'proxy_business_type' },
            clearable: true,
          },
          {
            type: 'daterange',
            prop: 'contractCreateDate',
            name: this.$t('contractAgency.orderMakeTime'),
          },
          {
            type: 'searchTableSpec',
            prop: 'contractSalesmanNo',
            name: this.$t('contractAgency.saleMan'),
            searchType: 'personnel',
            isMultiple: true,
          },
          {
            type: 'select', // 公司：操作人账户下公司
            name: this.$t('contractAgency.company'),
            prop: 'contractCompanyNo',
            options: this.companyList,
            changeMethod: () => {
              this.query.contractDepartmentNo = '';
            },
          },
          {
            type: 'ajaxSelect',
            prop: 'contractDepartmentNo',
            name: this.$t('contractAgency.businessDepartment'),
            apiUrl: departmentUrl,
            clearable: true,
            method: 'get',
            dataString: 'data.department',
            valueKey: 'createDepartmentCode',
            labelKey: 'createDepartmentName',
          },
          {
            type: 'searchTableSpec',
            name: this.$t('contractAgency.productDesc'),
            prop: 'productDesc',
            searchType: 'goodsDetail',
            isMultiple: true,
          },
          {
            type: 'inputRange',
            prop: ['contractQuantityStart', 'contractQuantityEnd'],
            precision: 2, //精度，就是保留几位小数,默认两位
            max: 99999999999999.99,
            name: this.$t('contractAgency.contractNumber'),
          },
          {
            type: 'dictSelect',
            prop: 'contractStatus',
            name: this.$t('contractAgency.receiptStatus'),
            apiData: { dictType: 'contract_request_status' },
            clearable: true,
          },
          {
            type: 'searchTableSpec',
            name: this.$t('contractAgency.warehouse'),
            prop: 'warehouse',
            searchType: 'warehouse',
            isMultiple: true,
          },
          {
            type: 'text',
            span: 24,
            text: this.$t('contractAgency.purchaseText'),
          },
          {
            type: 'searchTableSpec',
            name: this.$t('contractAgency.supplier'),
            prop: 'partnerNo',
            searchType: 'supplier',
            isMultiple: true,
            extraData: {
              partnerTypes: 'HZHB02,HZHB03', //供应商，合作伙伴
              departmentCode: this.query.contractDepartmentNo, //部门
              companyCode: this.query.contractCompanyNo, //公司
            },
          },
          {
            type: 'dictSelect',
            prop: 'purchasePayMethod',
            name: this.$t('contractAgency.payWay'),
            apiData: { dictType: 'payment_method' },
            clearable: true,
          },
          {
            type: 'inputRange',
            prop: ['purchaseBusinessUnitPriceStart', 'purchaseBusinessUnitPriceEnd'],
            precision: 2, //精度，就是保留几位小数,默认两位
            max: 99999999999999.99,
            name: this.$t('contractAgency.contractPrice'),
          },
          {
            type: 'date',
            prop: 'lastestPayMoneyDay',
            name: this.$t('contractAgency.payTimeLate'),
          },
          {
            type: 'date',
            prop: 'lastestReceiveProductDay',
            name: this.$t('contractAgency.getGoodLate'),
          },
          {
            type: 'dictSelect',
            prop: 'purchaseReceivingWay',
            name: this.$t('contractAgency.getGoodWay'),
            apiData: { dictType: 'instruction_type' },
            clearable: true,
          },
          {
            type: 'dictSelect',
            prop: 'purchaseDeliveryWay',
            name: this.$t('contractAgency.transportWay'),
            apiData: { dictType: 'delivery_method' },
            clearable: true,
          },
          {
            type: 'text',
            span: 24,
            text: this.$t('contractAgency.salesText'),
          },
          {
            type: 'searchTableSpec',
            name: this.$t('contractAgency.clientName'),
            prop: 'entrustingPartNo',
            searchType: 'customer',
            isMultiple: true,
            extraData: {
              partnerTypes: 'HZHB03,HZHB02,HZHB01', //合作伙伴，供应商，客户
              departmentCode: this.query.contractDepartmentNo, //部门
              companyCode: this.query.contractCompanyNo, //公司
            },
          },
          {
            type: 'dictSelect',
            prop: 'salesPayMethod',
            name: this.$t('contractAgency.payWay'),
            apiData: { dictType: 'payment_method' },
            clearable: true,
          },
          {
            type: 'inputRange',
            prop: ['proxyBusinessUnitPriceStart', 'proxyBusinessUnitPriceEnd'],
            precision: 2, //精度，就是保留几位小数,默认两位
            max: 99999999999999.99,
            name: this.$t('contractAgency.contractPrice'),
          },
          {
            type: 'date',
            prop: 'lastestReceiveMoneyDay',
            name: this.$t('contractAgency.receiveTimeLate'),
          },
          {
            type: 'date',
            prop: 'lastestSendProductDay',
            name: this.$t('contractAgency.shipLate'),
          },
          {
            type: 'dictSelect',
            prop: 'proxyDistributionWay',
            name: this.$t('contractAgency.shipWap'),
            apiData: { dictType: 'sale_instruction_type' },
            clearable: true,
          },
          {
            type: 'dictSelect',
            prop: 'proxyDeliveryWay',
            name: this.$t('contractAgency.transportWay'),
            apiData: { dictType: 'delivery_method' },
            clearable: true,
          },
          {
            type: 'text',
            span: 24,
            text: ' ',
          },
          {
            type: 'inputRange',
            prop: ['proxyExpenseStart', 'proxyExpenseEnd'],
            precision: 2, //精度，就是保留几位小数,默认两位
            max: 99999999999999.99,
            name: this.$t('contractAgency.agencyFeeSearch'),
          },
          {
            type: 'inputRange',
            prop: ['marginAmountStart', 'marginAmountEnd'],
            precision: 2, //精度，就是保留几位小数,默认两位
            max: 99999999999999.99,
            name: this.$t('contractAgency.depositCashSearch'),
          },
          {
            prop: 'originalsReceivedBusiness',
            name: '正本收集状态',
            type: 'dict',
            dictType: 'originalsReceivedBusiness',
            placeholder: '请输入正本收集状态',
          },
          {
            prop: 'originalsReceivedRm',
            name: '正本归档状态',
            type: 'dict',
            dictType: 'originalsReceivedRm',
            placeholder: '请输入正本归档状态',
          },
        ];
      },
    },
    mounted() {
      this.initQuery();
      this.$nextTick(() => {
        this.getData();
        this.getButtonList();
      });
    },
    methods: {
      handlePath,
      initQuery() {
        const query = {};
        this.searchColumns.forEach((item) => {
          if (item.prop) {
            if (typeof item.prop === 'string') {
              query[item.prop] = undefined;
            } else {
              item.prop.forEach((prop) => {
                query[prop] = undefined;
              });
            }
          }
        });
        this.query = Object.assign({}, query, this.query);
      },
      getQuery() {
        // 分页数据
        const { pageNum, pageSize } = this.$refs.siruiTable.page;
        const query = this.query;
        const result = Object.assign({}, query, {
          dataGroup: this.$globalVar.dataGroup,
          pageNum,
          pageSize,
          contractCreateDateStart: query.contractCreateDate ? query.contractCreateDate[0] : '',
          contractCreateDateEnd: query.contractCreateDate ? query.contractCreateDate[1] : '',
          contractSalesmanNo:
            query.contractSalesmanNo && query.contractSalesmanNo[0]
              ? query.contractSalesmanNo[0].id
              : '',
          productNo: this.handleData(query.productDesc, 'productDetailCode'), //商品描述
          partnerNo: this.handleData(query.partnerNo, 'partnerCode'), //供应商
          entrustingPartNo: this.handleData(query.entrustingPartNo, 'partnerCode'), //委托方
          storageNo: query.warehouse && query.warehouse[0] ? query.warehouse[0].storageCode : '',
        });
        delete result.productDesc;
        delete result.contractCreateDate;
        return filterEmpty(result);
      },
      //处理数据
      handleData(multipleSelection, key) {
        let selectList = multipleSelection ? multipleSelection : [];
        let list = [];
        selectList.forEach((item) => {
          if (key == 'partnerCode') {
            if (item.allPartnerCode && item['allPartnerCode'] != undefined) {
              list.push(item.allPartnerCode);
            } else {
              list.push(item.partnerCode);
            }
          } else if (key == 'productDetailCode') {
            //合并的数据
            if (item.allProductDetailCode && item['allProductDetailCode'] != undefined) {
              list.push(item.allProductDetailCode);
            } else {
              list.push(item.productDetailCode);
            }
          }
        });
        return list.length != 0 ? list.join(',') : null;
      },
      // 处理批量提交
      handleSubmit() {
        const selectedList = this.multipleSelection; // 选中数据
        // 只能提交已保存和已驳回状态的
        const contractStatus = selectedList.some((item) => {
          return item.contractStatus != 'ZT06' && item.contractStatus != 'ZT05';
        });
        if (selectedList.length < 1 || contractStatus) {
          let message = this.$t('contractAgency.errorTips.submitEmptyTip');
          if (contractStatus) message = this.$t('contractAgency.errorTips.submitStatusTip');
          this.$message({ showClose: true, message, type: 'error' });
          return;
        }
        const contractNos = [];
        selectedList.forEach((item) => {
          contractNos.push(item.contractNo);
        });
        const params = {
          contractNos,
        };
        this.loading.startLoading();
        this.$axios.post(AGENCY_API.BATCH_AUDIT, params).then((res) => {
          this.loading.endLoading();
          if (res.data.code === 0) {
            this.getData();
            this.$message({
              showClose: true,
              message: res.data.msg,
              type: 'success',
            });
          } else {
            this.$alert(res.data.msg);
            // 部分数据提交成功，刷新数据
            if (res.data.code == 4010) {
              this.getData();
            }
          }
        });
      },
      // 处理删除
      handleDelete() {
        const selectedList = this.multipleSelection; // 选中数据
        // 只能删除已保存状态的，其他状态给提示
        const contractStatus = selectedList.some((item) => {
          return !(item.contractStatus == 'ZT06' || item.contractStatus == 'ZT05');
        });
        if (selectedList.length < 1 || contractStatus) {
          let message = this.$t('contractAgency.errorTips.deleteEmptyTip');
          if (contractStatus) message = this.$t('contractAgency.errorTips.deleteStatusTip');
          this.$message({ showClose: true, message, type: 'error' });
          return;
        }
        this.$confirmAction()
          .then(() => {
            const ids = [];
            const contractNos = [];
            selectedList.forEach((item) => {
              ids.push(item.id);
              contractNos.push(item.contractNo);
            });
            const params = {
              ids,
              contractNos,
            };
            this.$axios.post(AGENCY_API.BATCH_DELETE_ORDER, params).then((res) => {
              if (res.data.code === 0) {
                this.getData();
                this.$refs.siruiTable.reloadTable();
                this.$message({
                  showClose: true,
                  message: res.data.msg,
                  type: 'success',
                });
              } else {
                this.$alert(res.data.msg);
              }
            });
          })
          .catch(() => {});
      },
      // 处理删除申请
      handleDeleteCommit() {
        const selectedList = this.multipleSelection; // 选中数据

        if (selectedList.length < 1) {
          let message = '请至少选择一条数据进行操作';
          this.$message({ showClose: true, message, type: 'error' });
          return;
        }
        this.$confirmAction('提交删除申请', '是否确认提交删除申请？')
          .then(() => {
            // const ids = [];
            const contractNos = [];
            selectedList.forEach((item) => {
              contractNos.push(item.contractNo);
            });
            const params = {
              contractNos,
            };
            this.$axios.post(AGENCY_API.DELETE_CONTRACT_COMMIT, params).then((res) => {
              if (res.data.code === 0) {
                this.getData();
                this.$refs.siruiTable.reloadTable();
                this.$message({
                  showClose: true,
                  message: res.data.msg,
                  type: 'success',
                });
              } else {
                this.$alert(res.data.msg);
              }
            });
          })
          .catch(() => {});
      },
      handleCopy() {
        const selectedList = this.multipleSelection; // 选中数据
        const row = selectedList[0];
        // 只能复制审批完成的：已生效
        if (selectedList.length != 1 || (row && row.contractStatus != 'ZT03')) {
          let message = this.$t('contractAgency.errorTips.copyEmptyTip');
          if (selectedList.length == 1 && row && row.contractStatus != 'ZT03')
            message = this.$t('contractAgency.errorTips.copyStatusTip');
          this.$message({ showClose: true, message, type: 'error' });
          return;
        }
        this.handlePath('add', row.contractNo, true);
      },
      handleExport() {
        const tableColumn = this.$refs.siruiTable.getTableColumn();
        const showColumns = [];
        tableColumn.visibleColumn.forEach((item) => {
          if (item.property) {
            showColumns.push({ prop: item.property, label: item.title });
          }
        });
        // 没有选择列
        if (tableColumn < 1) {
          let message = this.$t('contractAgency.errorTips.columnEmptyTip');
          this.$message({ showClose: true, message, type: 'error' });
          return;
        }
        const prevSelectedList = this.$refs.siruiTable.getCheckboxReserveRecords(); // 历史选中数据
        // 如果不选中，导出当前搜索条件下的数据
        const selectedList = this.multipleSelection.concat(prevSelectedList); // 选中数据
        const params = this.getQuery();
        params.idsExport = selectedList.map((item) => {
          return item.id;
        });
        // 导出字段
        params.columns = JSON.stringify(showColumns);
        // 获取数据
        this.$axios.post(AGENCY_API.EXPORT, filterEmpty(params)).then((res) => {
          if (res.data.code === 0) {
            this.$message({
              showClose: true,
              message: res.data.msg,
              type: 'success',
            });
            window.location.href = res.data.data;
          } else {
            this.$alert(res.data.msg);
          }
        });
      },
      chooseClick(type) {
        if (type == 'add') {
          this.handlePath('add');
          return;
        }
        if (type == 'delete') {
          this.handleDelete();
        }
        if (type == 'deleteApply') {
          this.handleDeleteCommit();
        }
        if (type == 'copy') {
          this.handleCopy();
        }
        if (type == 'export') {
          this.handleExport();
        }
        if (type == 'submit') {
          this.handleSubmit();
        } else if (type == 'originalsReceivedBusiness') {
          //正本收集
          this.originalsReceived('originalsReceivedBusiness');
        } else if (type == 'originalsReceivedRm') {
          //正本归档
          this.originalsReceived('originalsReceivedRm');
        }
      },

      //正本操作
      originalsReceived(type) {
        const selectedList = this.$refs.siruiTable
          .getCheckboxRecords()
          .concat(this.$refs.siruiTable.getCheckboxReserveRecords()); // 当前页选中
        console.log(selectedList);
        if (selectedList.length === 0) {
          this.$message({
            showClose: true,
            message: this.$t('xTable.noSelectData'),
            type: 'error',
          });
          return;
        }
        if (selectedList.find((item) => item.contractStatus !== 'ZT03')) {
          this.$message({
            showClose: true,
            message: '只有已生效合同才能操作！',
            type: 'error',
          });
          return;
        }
        this.$refs.OriginalsReceivedDialog.openDialog(true, type, {
          contractNo: selectedList.map((item) => item.contractNo),
        });
      },

      getData(search) {
        // 点击查询必须有条件：默认有分页、排序参数
        const params = this.getQuery();
        if (search && Object.keys(params).length == 2) {
          this.$message({
            showClose: true,
            message: this.$t('search.nosearchError'),
            type: 'error',
          });
          return;
        }
        if (search && Object.keys(params).length == 3 && params.orderByDesc) {
          this.$message({
            showClose: true,
            message: this.$t('search.nosearchError'),
            type: 'error',
          });
          return;
        }
        this.load = true;
        // 获取数据
        this.$axios
          .post(AGENCY_API.AGENCY_CONTRACT_LIST, params)
          .then((res) => {
            this.load = false;
            if (res.data.code === 0) {
              this.tableData = res.data.data.list;
              this.$refs.siruiTable.setPageTotal(res.data.data.total);
              // this.multipleSelection = [];
              // this.$refs.siruiTable.clearCheckboxReserve();
            }
          })
          .catch(() => {
            this.load = false;
          });
      },
      // 表格排序
      sortChangeEvent(order) {
        let orderByDesc = '';
        let property = '';
        if (order) {
          orderByDesc = `tot.${toLine(property)} ${order}`;
        }
        const query = Object.assign({}, this.query, {
          orderByDesc,
        });
        this.query = query;
        this.getData();
      },
      toPath(contractNo, id) {
        const pathName = 'agencyContractEdit';
        const title = this.$t('contractAgency.button.editClientBtn');
        let url = '/agencyContract/edit';
        let path = {
          path: url,
          name: pathName,
          meta: {
            keepAlive: true,
            title: title,
          },
          query: {
            id: id,
            contractNo: contractNo,
          },
        };
        this.$store.dispatch('tagsView/addTabView', path).then(() => {
          this.$nextTick(() => {
            this.$router.push(path);
          });
        });
      },
      // 列表双击
      dblclickEvent(value) {
        const row = value.row;
        this.toPath(row.contractNo, row.id);
      },
      // 重置
      resetBtnClick() {
        this.$refs.siruiTable.clearCheckboxReserve();
        this.$refs.siruiTable.setPageNum(1);
        this.$refs.siruiTable.setPageSize(10);
        this.query.orderByDesc = '';
        this.getData();
      },
    },
  };
</script>
